import { I18n } from '@calendly/i18n'
import { CloseIcon, CloseMiniIcon } from '@calendly/ui/components/icons'
import { BodyText } from '@calendly/ui/components/typography'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { colorTextActionContrast } from '@calendly/ui/theme'
import { useRef, MouseEvent, FC } from 'react'

import {
  ArrowImage,
  ButtonContainer,
  ProgressContainer,
  PrimaryButton,
  SecondaryButton,
  Spacer,
  StyledCloseButton,
  StyledHeadingText,
  SpotlightWrapper,
  ExtraContentContainer,
  ExtraContentImage,
  TextContainer
} from './spotlight.atoms'
import { useSpotlightPosition, useOutsideClickDetection } from './spotlight.hook'
import { THEMES } from './themes'

type PlacementType =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'left'
  | 'left-top'
  | 'left-bottom'
  | 'right'
  | 'right-top'
  | 'right-bottom'

type ThemeType = 'classic' | 'meeting_intelligence' | 'meeting_intelligence_simplified' | 'meeting_recap_tour'
type ButtonType = 'primary' | 'meeting-intelligence' | 'danger' | 'outline' | 'secondary-outline' | 'secondary-danger' | 'ghost'

export interface SpotlightProps {
  /** Callback when the close icon is clicked or (optionally) an outside click is detected. */
  onClose: () => void
  /** If `true`, clicking outside the spotlight will trigger `onClose`. */
  outsideClickDetectionEnabled?: boolean
  /** The preferred placement for the spotlight, though it may flip if there's not enough space. */
  placement?: PlacementType
  /** The primary offset distance (pixels) from the parent node. */
  offset?: number
  /** A secondary offset used for nuanced placements (e.g., top-left). */
  secondaryOffset?: number
  /** Optional heading text displayed near the top of the spotlight. */
  headingText?: string | null
  /** Optional body text or description. */
  bodyText: string
  /** Optional URL of an image displayed above the text content. */
  imageSource?: string
  /** Callback for the primary (right) button, if shown. */
  onClickPrimary?: () => void
  /** Label of the primary button. */
  primaryButtonLabel?: string
  /** Callback for the secondary (left) button, if shown. */
  onClickSecondary?: () => void
  /** Label of the secondary button. */
  secondaryButtonLabel?: string
  /** Optional icon to display on the secondary button. */
  secondaryButtonIcon?: FC<any>
  /** Additional text (e.g. progress info) rendered alongside buttons. */
  footerText?: string | null
  /** A theme key controlling stylings, e.g., 'classic', 'meeting_intelligence'. */
  theme?: ThemeType
  /** An additional offset for the arrow. */
  arrowOffset?: number
  /** If `true`, the spotlight will use customized design. */
  newCardDesign?: boolean
  /** If `true`, prevents automatic placement flipping when overflow is detected. */
  disableAutoFlip?: boolean
  /** Whether to show the close button. */
  showClose?: boolean
}

/**
 * Renders a spotlight component relative to its parent node.
 * This component automatically adjusts its position based on
 * the specified `placement` and available viewport space.
 *
 * Optionally, you can display an image (above the text content),
 * a heading, body text, and up to two buttons (primary & secondary).
 * A close icon is always rendered.
 *
 * @example
 * <Spotlight
 *   headingText="Welcome to Calendly"
 *   bodyText="Set up your first event type"
 *   placement="top-right"
 *   offset={24}
 *   outsideClickDetectionEnabled
 *   onClose={() => console.log('Spotlight closed!')}
 * />
 */

export const Spotlight: FC<SpotlightProps> = ({
  onClose,
  outsideClickDetectionEnabled,
  placement = 'right-bottom',
  offset = 24,
  secondaryOffset = 0,
  headingText = null,
  bodyText,
  imageSource,
  onClickPrimary,
  primaryButtonLabel = I18n.t('publisher.action.continue'),
  onClickSecondary,
  secondaryButtonLabel = I18n.t('publisher.action.back'),
  secondaryButtonIcon,
  footerText = null,
  theme = 'classic',
  arrowOffset = 0,
  newCardDesign = false,
  disableAutoFlip = false,
  showClose = true
}) => {
  const isPhone = useDeviceType('isPhone')
  const ref = useRef<HTMLDivElement>(null)
  const spotlight = useSpotlightPosition(ref, placement, offset, secondaryOffset, arrowOffset, disableAutoFlip)
  useOutsideClickDetection(ref, onClose, outsideClickDetectionEnabled)

  const PrimaryButtonIconComponent = THEMES[theme].primaryButtonIcon
  const SecondaryButtonIconComponent = secondaryButtonIcon || THEMES[theme].secondaryButtonIcon
  const bodyTextSize = THEMES[theme].bodyTextSize || 'medium'
  const bodyTextColor = THEMES[theme].useTextColor ? THEMES[theme].textColor : null
  const mobileMRSimplified = theme === 'meeting_intelligence_simplified' && isPhone

  return (
    <SpotlightWrapper
      ref={ref}
      {...spotlight.position}
      theme={theme}
      data-testid="spotlight"
      mobileMRSimplified={mobileMRSimplified}
      classic={theme === 'classic'}
      onClick={(e: MouseEvent<HTMLDivElement>) => { e.stopPropagation() }}
    >
      {showClose && (
        <StyledCloseButton
          onClick={onClose}
          aria-label={I18n.t('publisher.action.close')}
          hasExtraContent={!!imageSource}
        >
          {newCardDesign ? <CloseMiniIcon /> : <CloseIcon />}
        </StyledCloseButton>
      )}
      <ArrowImage {...spotlight.arrowPosition} theme={theme} placement={spotlight.actualPrimaryPlacement} />
      {imageSource && (
        <ExtraContentContainer>
          <ExtraContentImage src={imageSource} alt="" />
        </ExtraContentContainer>
      )}
      <TextContainer isExtended={!!THEMES[theme].isExtended}>
        {headingText && (<StyledHeadingText>{headingText}</StyledHeadingText>)}
        <Spacer />
        <BodyText size={bodyTextSize} color={bodyTextColor}>{bodyText}</BodyText>
      </TextContainer>
      {(onClickPrimary || onClickSecondary) && (
        <ButtonContainer theme={theme} newCardDesign={newCardDesign}>
          {footerText && (
            <ProgressContainer>
              <BodyText size="medium" color="muted">
                {footerText}
              </BodyText>
            </ProgressContainer>
          )}
          {onClickSecondary && (
            <SecondaryButton
              theme={theme}
              size="small"
              decoration="ghost"
              onClick={onClickSecondary}
              color={newCardDesign ? colorTextActionContrast : THEMES[theme].secondaryButtonColor}
            >
              {!!SecondaryButtonIconComponent && <SecondaryButtonIconComponent />}
              {secondaryButtonLabel}
            </SecondaryButton>
          )}
          {onClickPrimary && (
            <PrimaryButton
              theme={theme}
              size="small"
              decoration={THEMES[theme].primaryButtonType as ButtonType}
              onClick={onClickPrimary}
              spacingLeft={12}
            >
              {!!PrimaryButtonIconComponent && <PrimaryButtonIconComponent alt="" color={THEMES[theme].primaryButtonIconColor} />}
              {primaryButtonLabel}
            </PrimaryButton>
          )}
        </ButtonContainer>
      )}
    </SpotlightWrapper>
  )
}
